// noinspection ES6MissingAwait

import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useMount, useUnmount } from "react-use";
import { useMediaQuery } from "@mui/material";

import { BackdropWithSpinner } from "@/components/atoms";
import { conversationApi } from "@/shared/apis";
import { CONVERSATION_NEW_ID, ConversationsPageState } from "@/shared/domain";
import { useAuth, useNavigation, usePolicyholders, useRouterHook, useToast } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import { ChatInputFormType } from "../components/ConversationMessageChat/components/ConversationChatInput/ConversationChatInput";
import ConversationMessageChat from "../components/ConversationMessageChat/ConversationMessageChat";
import NewConversationMessageChat from "../components/ConversationMessageChat/NewConversationMessageChat";
import ConversationPreviewList from "../components/ConversationPreviewList/ConversationPreviewList";
import { StyledConversationPage, StyledMainConversation } from "./styled";

const ConversationsPage: FC = () => {
    const {
        state: { title, objectId, objectType, policyholderId: statePolicyholderId },
    } = useRouterHook<ConversationsPageState>();
    const isSmallScreen = !useMediaQuery("(min-width:768px)");
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const { errorToast } = useToast();
    const [topic, setTopic] = useState("");
    const [message, setMessage] = useState("");
    const { incUsageCount } = useUserFeedback();
    const { t } = useTranslation();
    const [isPosting, setIsPosting] = useState(false);
    const { navToConversationDetail, navToConversations, navToRoot } = useNavigation();
    const { id: conversationId } = useParams<{ id: string }>();

    const [selectedConversation, setSelectedConversation] = useState<MyBrokerApi.Conversation>(null);
    const policyholderId = statePolicyholderId || user.policyHolderId;
    const { getPolicyholder } = usePolicyholders();
    const policyholder = getPolicyholder(policyholderId);

    const { data: conversations } = useQuery("conversations", () => conversationApi.getConversations());
    const { data: fetchedConversation, isFetching: isFetchingConversation } = useQuery(
        ["conversation", { id: selectedConversation?.id }],
        () => conversationApi.getConversation(selectedConversation?.id),
        {
            suspense: false,
            enabled: !!selectedConversation?.id,
        },
    );

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("conversation", "Conversations");
    });

    useUnmount(() => {
        queryClient.invalidateQueries("unreadMessages");
    });

    const handleBackToConversationList = () => {
        setSelectedConversation(null);
        navToConversations();
    };

    const handleNewConversation = () => {
        setSelectedConversation(null);
        navToConversationDetail({
            conversationId: CONVERSATION_NEW_ID,
            policyholderId: policyholderId,
            title: t("Conversation.NewConversation"),
        });
    };

    const handleOpenMessage = (conversation: MyBrokerApi.Conversation) => {
        setSelectedConversation(conversation);
        navToConversationDetail({
            conversationId: conversation.id,
            policyholderId: conversation.file?.id,
            title: conversation.topic,
        });
        queryClient.invalidateQueries("unreadMessages");
    };

    const onPostFailure = () => {
        setIsPosting(false);
        errorToast({
            message: t("Conversation.Errors.PostFailure"),
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        return false;
    };

    const onSubmitForm = async (inputData: ChatInputFormType) => {
        const data: ChatInputFormType = {
            ...inputData,
            sender: { name: policyholder?.fullName },
        };
        setIsPosting(true);
        if (conversationId && conversationId !== CONVERSATION_NEW_ID) {
            try {
                const result = await conversationApi.postMessage(conversationId, data);
                if (result.status === 200) {
                    queryClient.invalidateQueries(["conversation", { id: conversationId }]);
                    queryClient.invalidateQueries("conversations");
                } else {
                    return onPostFailure();
                }
            } catch (e) {
                return onPostFailure();
            }
        } else {
            try {
                const context = objectType != null ? { topic: title, id: objectId, type: objectType } : null;
                const result = await conversationApi.createConversation(policyholderId, data, context);
                if (result.status === 200) {
                    queryClient.invalidateQueries("conversations").then(() =>
                        navToConversationDetail({
                            conversationId: result.data.id,
                            policyholderId: result.data.file?.id,
                            title: result.data.topic,
                        }),
                    );
                } else {
                    return onPostFailure();
                }
            } catch (e) {
                return onPostFailure();
            }
        }
        setIsPosting(false);
        return true;
    };

    useEffect(() => {
        if (conversations != null && conversations.length > 0 && selectedConversation == null) {
            if (conversationId === undefined && !isSmallScreen) {
                handleOpenMessage(conversations[0]);
            } else if (conversationId !== undefined && conversationId !== CONVERSATION_NEW_ID) {
                const foundConversation = conversations.find((conversation) => conversation.id === conversationId);
                if (foundConversation != null) {
                    setSelectedConversation(foundConversation);
                } else {
                    log.info("Conversation not found !");
                    handleNewConversation();
                }
            }
        } else if (
            conversations != null &&
            conversations.length === 0 &&
            selectedConversation == null &&
            conversationId !== CONVERSATION_NEW_ID
        ) {
            handleNewConversation();
        }
    }, [conversations]);

    return (
        <StyledMainConversation hideHeader={isSmallScreen} title={t("PageTitles.Conversations")}>
            {isPosting && <BackdropWithSpinner />}
            <StyledConversationPage>
                {(!isSmallScreen ||
                    (isSmallScreen && !selectedConversation && conversationId !== CONVERSATION_NEW_ID)) && (
                    <ConversationPreviewList
                        newMessageTitle={objectType != null ? title : topic}
                        newMessageContent={message}
                        conversationId={conversationId}
                        selectedConversation={selectedConversation}
                        onMessagePreview={handleOpenMessage}
                        onNewConversation={handleNewConversation}
                        conversations={conversations}
                        onBackPressed={isSmallScreen ? navToRoot : undefined}
                    />
                )}
                {conversationId === CONVERSATION_NEW_ID && (
                    <NewConversationMessageChat
                        topic={topic}
                        setTopic={setTopic}
                        setMessage={setMessage}
                        onBackPressed={isSmallScreen ? handleBackToConversationList : undefined}
                        objectTitle={objectType != null ? title : null}
                        onSubmitForm={onSubmitForm}
                        policyholderId={policyholderId}
                        policyholderName={policyholder?.fullName}
                    />
                )}
                {selectedConversation && (
                    <ConversationMessageChat
                        onBackPressed={isSmallScreen ? handleBackToConversationList : undefined}
                        conversation={fetchedConversation}
                        loading={isFetchingConversation}
                        onSubmitForm={onSubmitForm}
                    />
                )}
                {!isSmallScreen && (!conversationId || conversationId === "") && (
                    <div className="no-conversation-selected">
                        <span>{t("Conversation.NoConversationSelected")}</span>
                    </div>
                )}
            </StyledConversationPage>
        </StyledMainConversation>
    );
};

export default ConversationsPage;
